import { useState } from "react";
import type { StepView } from "@/app/queries";
import { Callout } from "@/ui/Callout";
import { Inline } from "./Inline";

type StuckData = StepView["content"]["stuck"];

/** «Если застрял» (S4): свёрнутый блок под практикой — сколько это обычно
 *  занимает, что перечитать, подсказки по одной и частые ошибки. */
export function Stuck({ view }: { view: StepView }) {
  const s = view.content.stuck;
  if (!s.on) return null;
  return (
    <div id="stuck" className="scroll-mt-20" data-testid="stuck">
      <Callout kind="guide" tag="если застрял" title={s.estimate || "подсказки и частые ошибки"} defaultOpen={false}>
        <StuckBody stuck={s} showEstimate={false} />
      </Callout>
    </div>
  );
}

const H = "mb-1.5 font-mono text-[10.5px] uppercase tracking-[.05em] text-mut";

/** Содержимое «Если застрял» — общее для S4 и вкладки S8. */
export function StuckBody({ stuck, showEstimate = true }: { stuck: StuckData; showEstimate?: boolean }) {
  const [shown, setShown] = useState(0);
  const { hints, errors, prereqs } = stuck;
  return (
    <div className="flex flex-col gap-4">
      {showEstimate && stuck.estimate && (
        <div className="flex gap-2 text-[13px] text-ink">
          <span aria-hidden className="font-mono text-acc">
            ⏱
          </span>
          <span>{stuck.estimate}</span>
        </div>
      )}
      {prereqs.length > 0 && (
        <div>
          <div className={H}>что перечитать</div>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {prereqs.map((p) => (
              <li key={p.href + p.title}>
                <a href={p.href} className="font-mono text-[12.5px] text-acc no-underline hover:underline">
                  ↗ {p.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hints.length > 0 && (
        <div>
          <div className={H}>подсказки</div>
          {shown > 0 && (
            <ol className="m-0 mb-2.5 flex flex-col gap-2 pl-5">
              {hints.slice(0, shown).map((h, i) => (
                <li key={i} className="text-pretty text-[13px] leading-[1.55] text-ink2">
                  <Inline text={h} />
                </li>
              ))}
            </ol>
          )}
          {shown < hints.length && (
            <button
              type="button"
              onClick={() => setShown((n) => n + 1)}
              className="min-h-[32px] cursor-pointer rounded-sm border border-bd2 bg-transparent px-[11px] py-[6px] font-mono text-[11.5px] text-ink hover:border-acc hover:text-acc"
            >
              показать подсказку {shown + 1} из {hints.length}
            </button>
          )}
        </div>
      )}
      {errors.length > 0 && (
        <div>
          <div className={H}>частые ошибки</div>
          <div className="flex flex-col gap-2">
            {errors.map((e, i) => (
              <div key={i} className="text-pretty text-[13px] leading-[1.55] text-ink2" style={{ borderLeft: "2px solid var(--err)", paddingLeft: 10 }}>
                <span className="font-semibold text-ink">{e.t}</span>
                {e.d && (
                  <>
                    {" "}
                    — <Inline text={e.d} />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
